/**
 * Behavioral Reading Insights Engine
 * 
 * Computes deep behavioral insights from user reading data,
 * going beyond vanity metrics to reveal patterns that make
 * users feel understood.
 */

import { parseISO, getDay, differenceInDays, format } from 'date-fns';

// ============= Types =============

export interface UserBookWithDetails {
  id: string;
  book_id: string;
  started_at?: string | null;
  finished_at?: string | null;
  rating_star?: number | null;
  shelf: string;
  reading_format?: string | null;
  reading_progress_percent?: number | null;
  books: {
    id: string;
    title: string;
    authors?: string[] | null;
    page_count?: number | null;
    subjects?: string[] | null;
  };
}

export interface DayPattern {
  peakDay: string;
  peakPercentage: number;
  distribution: number[]; // [Sun, Mon, Tue, Wed, Thu, Fri, Sat]
  isWeekendReader: boolean;
  totalBooks: number;
}

export interface PageCountInsight {
  sweetSpotRange: [number, number];
  avgRatingInRange: number;
  avgRatingOutside: number;
  completionSpeedInRange: number; // days
  completionSpeedOutside: number; // days
  dnfRateAbove: number; // percentage for books above sweet spot
  threshold: number; // page count threshold for DNF analysis
  hasEnoughData: boolean;
}

export interface FormatInsight {
  mostUsedFormat: string;
  formatCompletion: Record<string, { completed: number; total: number; rate: number }>;
  fastestFormat: string;
  fastestFormatSpeed: number; // days
  secondFormat?: string;
  secondFormatRate?: number;
  hasEnoughData: boolean;
}

export interface DNFInsight {
  overallDNFRate: number;
  avgAbandonmentPoint: number; // percentage
  genreWithHighestDNF?: string;
  genreDNFRate?: number;
  pageLengthCorrelation: 'positive' | 'negative' | 'none';
  totalDNFs: number;
  hasEnoughData: boolean;
}

export interface GenreVelocityInsight {
  fastestGenre: string;
  fastestDays: number;
  slowestGenre: string;
  slowestDays: number;
  hasEnoughData: boolean;
}

export interface SeasonalPattern {
  peakMonth: string;
  peakCount: number;
  slumpMonth: string;
  slumpCount: number;
  hasEnoughData: boolean;
}

export interface BehavioralInsightsData {
  dayPattern?: DayPattern;
  pageCountInsight?: PageCountInsight;
  formatInsight?: FormatInsight;
  dnfInsight?: DNFInsight;
  genreVelocity?: GenreVelocityInsight;
  seasonalPattern?: SeasonalPattern;
}

// ============= Constants =============

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 
                     'July', 'August', 'September', 'October', 'November', 'December'];

// Page count brackets for analysis
const PAGE_BRACKETS = [
  { min: 0, max: 200, label: 'Under 200' },
  { min: 200, max: 350, label: '200-350' },
  { min: 350, max: 450, label: '350-450' },
  { min: 450, max: Infinity, label: '450+' }
];

// ============= Calculation Functions =============

/**
 * Analyze day-of-week completion patterns
 */
export function calculateDayPattern(completedBooks: UserBookWithDetails[]): DayPattern | undefined {
  const booksWithFinishDate = completedBooks.filter(b => b.finished_at);
  
  if (booksWithFinishDate.length < 10) return undefined;

  // Count books finished per day of week
  const distribution = [0, 0, 0, 0, 0, 0, 0]; // Sun-Sat
  
  booksWithFinishDate.forEach(book => {
    const dayOfWeek = getDay(parseISO(book.finished_at!));
    distribution[dayOfWeek]++;
  });

  const total = booksWithFinishDate.length;
  const peakIndex = distribution.indexOf(Math.max(...distribution));
  const peakPercentage = Math.round((distribution[peakIndex] / total) * 100);
  
  // Weekend = Saturday (6) + Sunday (0)
  const weekendCount = distribution[0] + distribution[6];
  const isWeekendReader = weekendCount / total > 0.5;

  return {
    peakDay: DAY_NAMES[peakIndex],
    peakPercentage,
    distribution,
    isWeekendReader,
    totalBooks: total
  };
}

/**
 * Analyze page count sweet spot - where ratings and speed are optimal
 */
export function calculatePageCountInsight(
  completedBooks: UserBookWithDetails[],
  dnfBooks: UserBookWithDetails[]
): PageCountInsight | undefined {
  const booksWithRatingAndPages = completedBooks.filter(
    b => b.rating_star && b.books.page_count && b.books.page_count > 0
  );

  if (booksWithRatingAndPages.length < 8) return undefined;

  // Group books by page count brackets and calculate average ratings
  const bracketData = PAGE_BRACKETS.map(bracket => {
    const booksInBracket = booksWithRatingAndPages.filter(
      b => b.books.page_count! >= bracket.min && b.books.page_count! < bracket.max
    );
    
    const avgRating = booksInBracket.length > 0
      ? booksInBracket.reduce((sum, b) => sum + b.rating_star!, 0) / booksInBracket.length
      : 0;

    // Calculate average completion speed for books with both dates
    const booksWithDates = booksInBracket.filter(b => b.started_at && b.finished_at);
    const avgSpeed = booksWithDates.length > 0
      ? booksWithDates.reduce((sum, b) => 
          sum + differenceInDays(parseISO(b.finished_at!), parseISO(b.started_at!)), 0
        ) / booksWithDates.length
      : 0;

    return {
      bracket,
      count: booksInBracket.length,
      avgRating,
      avgSpeed
    };
  });

  // Find the sweet spot (highest average rating with at least 2 books)
  const validBrackets = bracketData.filter(b => b.count >= 2);
  if (validBrackets.length === 0) return undefined;

  const sweetSpotBracket = validBrackets.reduce((best, current) => 
    current.avgRating > best.avgRating ? current : best
  );

  // Calculate stats for books outside sweet spot
  const outsideBrackets = bracketData.filter(
    b => b.bracket.label !== sweetSpotBracket.bracket.label && b.count > 0
  );
  const avgRatingOutside = outsideBrackets.length > 0
    ? outsideBrackets.reduce((sum, b) => sum + b.avgRating * b.count, 0) / 
      outsideBrackets.reduce((sum, b) => sum + b.count, 0)
    : 0;
  const avgSpeedOutside = outsideBrackets.length > 0
    ? outsideBrackets.filter(b => b.avgSpeed > 0)
        .reduce((sum, b) => sum + b.avgSpeed * b.count, 0) / 
      outsideBrackets.filter(b => b.avgSpeed > 0).reduce((sum, b) => sum + b.count, 0) || 0
    : 0;

  // Calculate DNF rate for long books (450+)
  const longBooksDNF = dnfBooks.filter(b => b.books.page_count && b.books.page_count >= 450);
  const longBooksCompleted = completedBooks.filter(b => b.books.page_count && b.books.page_count >= 450);
  const dnfRateAbove = (longBooksDNF.length + longBooksCompleted.length) > 0
    ? Math.round((longBooksDNF.length / (longBooksDNF.length + longBooksCompleted.length)) * 100)
    : 0;

  return {
    sweetSpotRange: [sweetSpotBracket.bracket.min, sweetSpotBracket.bracket.max],
    avgRatingInRange: Math.round(sweetSpotBracket.avgRating * 10) / 10,
    avgRatingOutside: Math.round(avgRatingOutside * 10) / 10,
    completionSpeedInRange: Math.round(sweetSpotBracket.avgSpeed),
    completionSpeedOutside: Math.round(avgSpeedOutside),
    dnfRateAbove,
    threshold: 450,
    hasEnoughData: true
  };
}

/**
 * Analyze reading format impact on completion
 */
export function calculateFormatInsight(
  completedBooks: UserBookWithDetails[],
  dnfBooks: UserBookWithDetails[]
): FormatInsight | undefined {
  const allBooks = [...completedBooks, ...dnfBooks];
  const validFormats = ['print', 'digital', 'audio'];
  const booksWithFormat = allBooks.filter(b => b.reading_format && validFormats.includes(b.reading_format));

  if (booksWithFormat.length < 5) return undefined;

  // Count by format
  const formatStats: Record<string, { completed: number; dnf: number; totalDays: number; booksWithSpeed: number }> = {};

  booksWithFormat.forEach(book => {
    const fmt = book.reading_format!;
    if (!formatStats[fmt]) {
      formatStats[fmt] = { completed: 0, dnf: 0, totalDays: 0, booksWithSpeed: 0 };
    }

    if (book.shelf === 'read') {
      formatStats[fmt].completed++;
      
      // Track reading speed
      if (book.started_at && book.finished_at) {
        const days = differenceInDays(parseISO(book.finished_at), parseISO(book.started_at));
        formatStats[fmt].totalDays += days;
        formatStats[fmt].booksWithSpeed++;
      }
    } else if (book.shelf === 'unfinished') {
      formatStats[fmt].dnf++;
    }
  });

  // Find most used format
  const formats = Object.entries(formatStats);
  const mostUsedFormat = formats.reduce((best, [fmt, stats]) => {
    const total = stats.completed + stats.dnf;
    const bestTotal = formatStats[best]?.completed + formatStats[best]?.dnf || 0;
    return total > bestTotal ? fmt : best;
  }, formats[0][0]);

  // Calculate completion rates
  const formatCompletion: Record<string, { completed: number; total: number; rate: number }> = {};
  formats.forEach(([fmt, stats]) => {
    const total = stats.completed + stats.dnf;
    formatCompletion[fmt] = {
      completed: stats.completed,
      total,
      rate: Math.round((stats.completed / total) * 100)
    };
  });

  // Find fastest format
  const formatsWithSpeed = formats.filter(([, stats]) => stats.booksWithSpeed > 0);
  const fastestFormat = formatsWithSpeed.length > 0
    ? formatsWithSpeed.reduce((best, [fmt, stats]) => {
        const avgDays = stats.totalDays / stats.booksWithSpeed;
        const bestAvg = formatStats[best]?.totalDays / formatStats[best]?.booksWithSpeed || Infinity;
        return avgDays < bestAvg ? fmt : best;
      }, formatsWithSpeed[0][0])
    : mostUsedFormat;

  const fastestFormatSpeed = formatStats[fastestFormat]?.booksWithSpeed > 0
    ? Math.round(formatStats[fastestFormat].totalDays / formatStats[fastestFormat].booksWithSpeed)
    : 0;

  // Find second format for comparison
  const sortedFormats = Object.entries(formatCompletion)
    .sort((a, b) => b[1].rate - a[1].rate);
  const secondFormat = sortedFormats.length > 1 ? sortedFormats[1][0] : undefined;
  const secondFormatRate = secondFormat ? formatCompletion[secondFormat].rate : undefined;

  return {
    mostUsedFormat: formatLabel(mostUsedFormat),
    formatCompletion,
    fastestFormat: formatLabel(fastestFormat),
    fastestFormatSpeed,
    secondFormat: secondFormat ? formatLabel(secondFormat) : undefined,
    secondFormatRate,
    hasEnoughData: true
  };
}

/**
 * Analyze DNF patterns
 */
export function calculateDNFInsight(
  dnfBooks: UserBookWithDetails[],
  completedBooks: UserBookWithDetails[]
): DNFInsight | undefined {
  if (dnfBooks.length < 3) return undefined;

  const totalBooks = dnfBooks.length + completedBooks.length;
  const overallDNFRate = Math.round((dnfBooks.length / totalBooks) * 100);

  // Average abandonment point
  const booksWithProgress = dnfBooks.filter(b => b.reading_progress_percent && b.reading_progress_percent > 0);
  const avgAbandonmentPoint = booksWithProgress.length > 0
    ? Math.round(booksWithProgress.reduce((sum, b) => sum + b.reading_progress_percent!, 0) / booksWithProgress.length)
    : 0;

  // Genre with highest DNF
  const genreDNFCounts: Record<string, { dnf: number; completed: number }> = {};
  
  [...dnfBooks, ...completedBooks].forEach(book => {
    book.books.subjects?.forEach(genre => {
      if (!genreDNFCounts[genre]) {
        genreDNFCounts[genre] = { dnf: 0, completed: 0 };
      }
      if (book.shelf === 'unfinished') {
        genreDNFCounts[genre].dnf++;
      } else {
        genreDNFCounts[genre].completed++;
      }
    });
  });

  // Find genre with highest DNF rate (min 2 books)
  const genresWithEnoughData = Object.entries(genreDNFCounts)
    .filter(([, stats]) => stats.dnf + stats.completed >= 2 && stats.dnf > 0);
  
  let genreWithHighestDNF: string | undefined;
  let genreDNFRate: number | undefined;
  
  if (genresWithEnoughData.length > 0) {
    const highest = genresWithEnoughData.reduce((best, [genre, stats]) => {
      const rate = stats.dnf / (stats.dnf + stats.completed);
      const bestRate = genreDNFCounts[best[0]].dnf / (genreDNFCounts[best[0]].dnf + genreDNFCounts[best[0]].completed);
      return rate > bestRate ? [genre, stats] : best;
    });
    genreWithHighestDNF = highest[0];
    genreDNFRate = Math.round((highest[1].dnf / (highest[1].dnf + highest[1].completed)) * 100);
  }

  // Page length correlation
  const avgPagesDNF = dnfBooks.filter(b => b.books.page_count)
    .reduce((sum, b) => sum + b.books.page_count!, 0) / 
    dnfBooks.filter(b => b.books.page_count).length || 0;
  
  const avgPagesCompleted = completedBooks.filter(b => b.books.page_count)
    .reduce((sum, b) => sum + b.books.page_count!, 0) / 
    completedBooks.filter(b => b.books.page_count).length || 0;

  let pageLengthCorrelation: 'positive' | 'negative' | 'none' = 'none';
  if (avgPagesDNF > avgPagesCompleted * 1.2) {
    pageLengthCorrelation = 'positive'; // Longer books = more DNFs
  } else if (avgPagesDNF < avgPagesCompleted * 0.8) {
    pageLengthCorrelation = 'negative'; // Shorter books = more DNFs (unusual)
  }

  return {
    overallDNFRate,
    avgAbandonmentPoint,
    genreWithHighestDNF,
    genreDNFRate,
    pageLengthCorrelation,
    totalDNFs: dnfBooks.length,
    hasEnoughData: true
  };
}

/**
 * Analyze genre reading velocity
 */
export function calculateGenreVelocity(completedBooks: UserBookWithDetails[]): GenreVelocityInsight | undefined {
  const booksWithDatesAndGenres = completedBooks.filter(
    b => b.started_at && b.finished_at && b.books.subjects && b.books.subjects.length > 0
  );

  if (booksWithDatesAndGenres.length < 6) return undefined;

  // Group by genre and calculate average speed
  const genreSpeeds: Record<string, { totalDays: number; count: number }> = {};

  booksWithDatesAndGenres.forEach(book => {
    const days = differenceInDays(parseISO(book.finished_at!), parseISO(book.started_at!));
    
    book.books.subjects?.forEach(genre => {
      if (!genreSpeeds[genre]) {
        genreSpeeds[genre] = { totalDays: 0, count: 0 };
      }
      genreSpeeds[genre].totalDays += days;
      genreSpeeds[genre].count++;
    });
  });

  // Filter to genres with at least 3 books
  const validGenres = Object.entries(genreSpeeds)
    .filter(([, stats]) => stats.count >= 3);

  if (validGenres.length < 2) return undefined;

  // Find fastest and slowest
  const sorted = validGenres
    .map(([genre, stats]) => ({
      genre,
      avgDays: stats.totalDays / stats.count
    }))
    .sort((a, b) => a.avgDays - b.avgDays);

  const fastest = sorted[0];
  const slowest = sorted[sorted.length - 1];

  // Only show if there's meaningful difference (at least 2x)
  if (slowest.avgDays < fastest.avgDays * 1.5) return undefined;

  return {
    fastestGenre: fastest.genre,
    fastestDays: Math.round(fastest.avgDays),
    slowestGenre: slowest.genre,
    slowestDays: Math.round(slowest.avgDays),
    hasEnoughData: true
  };
}

/**
 * Analyze seasonal reading patterns
 */
export function calculateSeasonalPattern(completedBooks: UserBookWithDetails[]): SeasonalPattern | undefined {
  const booksWithFinishDate = completedBooks.filter(b => b.finished_at);
  
  if (booksWithFinishDate.length < 12) return undefined;

  // Count books per month
  const monthCounts = new Array(12).fill(0);
  
  booksWithFinishDate.forEach(book => {
    const month = parseISO(book.finished_at!).getMonth();
    monthCounts[month]++;
  });

  // Find peak and slump
  const maxCount = Math.max(...monthCounts);
  const minCount = Math.min(...monthCounts.filter(c => c > 0)); // Exclude zero months
  
  const peakMonth = MONTH_NAMES[monthCounts.indexOf(maxCount)];
  const slumpMonth = MONTH_NAMES[monthCounts.indexOf(minCount)];

  // Only show if there's meaningful variation
  if (maxCount < minCount * 1.5) return undefined;

  return {
    peakMonth,
    peakCount: maxCount,
    slumpMonth,
    slumpCount: minCount,
    hasEnoughData: true
  };
}

// ============= Main Calculation Function =============

/**
 * Calculate all behavioral insights from user books data
 */
export function calculateBehavioralInsights(
  completedBooks: UserBookWithDetails[],
  dnfBooks: UserBookWithDetails[]
): BehavioralInsightsData {
  return {
    dayPattern: calculateDayPattern(completedBooks),
    pageCountInsight: calculatePageCountInsight(completedBooks, dnfBooks),
    formatInsight: calculateFormatInsight(completedBooks, dnfBooks),
    dnfInsight: calculateDNFInsight(dnfBooks, completedBooks),
    genreVelocity: calculateGenreVelocity(completedBooks),
    seasonalPattern: calculateSeasonalPattern(completedBooks)
  };
}

// ============= Helpers =============

function formatLabel(format: string): string {
  const labels: Record<string, string> = {
    print: 'Print books',
    digital: 'E-books',
    audio: 'Audiobooks'
  };
  return labels[format] || format;
}
